import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, buildQueryParams, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { CONTRACT_HEADER } from '../handlers/type-normalization-handler.ts';
import { camelizeAttributes } from '../utils/camelize-attributes.ts';

/** Why a listed hold cannot be reduced from the held-funds dialog. */
export type HeldAssistanceReason = 'NOT_RESERVED' | 'NO_SHARED_LIMIT';

/**
 * One hold on another of the employee's applications, as seen from the application that
 * needs room. Amounts are integer cents.
 */
export interface HeldAssistance {
  assistanceId: string;
  applicationId: string;
  applicationName: string | null;
  programName: string | null;
  applicationState: string;
  manner: string;
  state: string;
  amount: number;
  /** ISO date the hold was placed; null when the transition log never recorded it. */
  heldDate: string | null;
  reducible: boolean;
  reasonCode: HeldAssistanceReason | null;
  /** Null when not reducible; a release of d frees exactly min(d, maxFreeable). */
  maxFreeable: number | null;
}

export interface HeldFunds {
  applicationId: string;
  /** The target application's remaining benefit in cents; null when unlimited. */
  available: number | null;
  softLimit: string | null;
  annualStartDate: string | null;
  annualEndDate: string | null;
  asOfDate: string | null;
  rows: HeldAssistance[];
}

interface HeldFundsResource {
  id: string;
  type: string;
  attributes?: Record<string, unknown>;
}

interface HeldFundsDocument {
  data?: HeldFundsResource | HeldFundsResource[];
  meta?: Record<string, unknown>;
}

/** The limit window both endpoints judge a release in; pass what the page's status call used. */
export interface HeldFundsWindow {
  /** Local `YYYY-MM-DD` — required by the reduce endpoint. */
  date: string;
  product: string;
}

const heldFundsUrl = () => {
  const options: QueryUrlOptions = {
    identifier: { type: 'held-assistances' },
    op: 'query',
    resourcePath: 'api/v2/limits/held-funds',
  };
  return buildBaseURL(options);
};

// Target contract only: a header-less request is refused with 406.
const contractHeaders = () => new Headers({ [CONTRACT_HEADER]: '2' });

/**
 * GET /api/v2/limits/held-funds — the held assistances on the employee's other
 * applications, and what releasing each would free for `applicationId`. Sent with
 * `X-Tio-Contract: 2` (the server refuses a header-less request with 406) and skips the cache.
 *
 * @param applicationId - the application that needs room
 * @param window - the date and product the page's limits status was read with
 */
export function heldFundsRequest(applicationId: string, window: HeldFundsWindow): RequestInfo {
  const params = buildQueryParams({
    application_id: applicationId,
    date: window.date,
    product: window.product,
  });
  return {
    url: `${heldFundsUrl()}?${params}`,
    method: 'GET',
    headers: contractHeaders(),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /api/v2/limits/held-funds/:assistanceId — lowers one hold so its money counts toward
 * `applicationId`. Rejects with 422 when a rule refuses it and 409 when the hold changed
 * underneath (e.g. a card authorization against the same reserve).
 *
 * @param assistanceId - the hold to lower
 * @param change - `amount` is the new amount in cents, below the current one; `reason` is
 *   recorded in the assistance's transition log
 * @param window - the same date and product the list was read with
 */
export function reduceHeldAssistanceRequest(
  assistanceId: string,
  change: { applicationId: string; amount: number; reason: string },
  window: HeldFundsWindow
): RequestInfo {
  return {
    url: `${heldFundsUrl()}/${assistanceId}`,
    method: 'PATCH',
    headers: contractHeaders(),
    body: JSON.stringify({
      application_id: change.applicationId,
      amount: change.amount,
      reason: change.reason,
      date: window.date,
      product: window.product,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

const text = (value: unknown) => (typeof value === 'string' ? value : null);
const cents = (value: unknown) => (typeof value === 'number' ? value : null);

/**
 * The request pipeline may have camelized the dasherized wire keys, so both spellings are
 * read the same way. Missing numbers read as null (and `amount` as 0).
 *
 * @param resource - one `held-assistances` resource object
 */
export function parseHeldAssistance(resource: HeldFundsResource): HeldAssistance {
  const attrs = camelizeAttributes(resource.attributes);
  return {
    assistanceId: resource.id,
    applicationId: String(attrs['applicationId'] ?? ''),
    applicationName: text(attrs['applicationName']),
    programName: text(attrs['programName']),
    applicationState: String(attrs['applicationState'] ?? ''),
    manner: String(attrs['manner'] ?? ''),
    state: String(attrs['state'] ?? ''),
    amount: cents(attrs['amount']) ?? 0,
    heldDate: text(attrs['heldDate']),
    reducible: attrs['reducible'] === true,
    reasonCode: text(attrs['reasonCode']) as HeldAssistanceReason | null,
    maxFreeable: cents(attrs['maxFreeable']),
  };
}

/**
 * @param content - the held-funds list document
 * @returns the rows plus the target application's remaining benefit and limit window
 */
export function parseHeldFunds(content: HeldFundsDocument): HeldFunds {
  const data = Array.isArray(content.data) ? content.data : [];
  const meta = camelizeAttributes(content.meta);
  return {
    applicationId: String(meta['applicationId'] ?? ''),
    available: cents(meta['available']),
    softLimit: text(meta['softLimit']),
    annualStartDate: text(meta['annualStartDate']),
    annualEndDate: text(meta['annualEndDate']),
    asOfDate: text(meta['asOfDate']),
    rows: data.map(parseHeldAssistance),
  };
}

/**
 * @param content - the single-resource document a successful reduce returns
 * @returns the hold as it now stands; its maxFreeable is what it can still free
 */
export function parseReducedHeldAssistance(content: HeldFundsDocument): HeldAssistance {
  return parseHeldAssistance(content.data as HeldFundsResource);
}

type JsonApiErrorEntry = { status?: unknown; detail?: unknown };
type ErrorLike = { status?: unknown; errors?: unknown; content?: { errors?: unknown } };

const errorEntries = (list: unknown): JsonApiErrorEntry[] =>
  Array.isArray(list)
    ? list.filter(
        (entry): entry is JsonApiErrorEntry => typeof entry === 'object' && entry !== null
      )
    : [];

/**
 * Reads a rejected reduce into what the dialog shows on the row. `detail` is the server's
 * own sentence for a 422 refusal; a 409 means the hold moved and the list needs reloading.
 * WarpDrive may throw an `AggregateError` carrying the JSON:API `errors[]` at the top level
 * (with string statuses) or an error holding the parsed body under `content.errors`; both
 * are read.
 *
 * @param error - whatever `store.request` rejected with
 */
export function reductionFailure(error: unknown): { status: number | null; detail: string | null } {
  const err = (error ?? {}) as ErrorLike;
  const entries = [...errorEntries(err.errors), ...errorEntries(err.content?.errors)];
  const detail = entries
    .map((entry) => entry.detail)
    .find((value) => typeof value === 'string' && value);
  const entryStatus = entries
    .map((entry) => Number(entry.status))
    .find((value) => Number.isInteger(value) && value > 0);
  return {
    status: typeof err.status === 'number' ? err.status : (entryStatus ?? null),
    detail: typeof detail === 'string' ? detail : null,
  };
}
